/**
 * PodStatusBadge — pastille statut d'un produit POD (S5.6).
 *
 * Wraps `<Badge>` avec mapping `PodProductStatus → BadgeVariant + label FR`.
 * Mapping (palette sgraphic — encre + rouge + papier, pas de vert/bleu) :
 *   draft     → default + dot         "Brouillon"
 *   uploading → info    + dot animé   "Préparation…"
 *   ready     → success + dot         "Prêt"
 *   failed    → danger                "Échec"
 *   archived  → default               "Archivé"
 *
 * Les drafts/uploading/ready ont un `dot` pour signaler l'activité ;
 * archived/failed sont sans dot (la couleur de variant suffit).
 *
 * Reste un composant **applicatif** (pas une primitive DS) — colocalisé
 * dans `components/pod/` comme `<PodConnectionCard>`. Pas de classe
 * `.ui-*` redéfinie hors `globals.css`.
 */

import Badge from '@/components/ui/Badge'
import type { BadgeVariant } from '@/components/ui/Badge'
import { POD_PRODUCT_STATUS_FR, type PodProductStatus } from '@/types/pod'

interface PodStatusBadgeProps {
  status: PodProductStatus
  /** Provider display_name affiché en title pour tooltip ("Prêt chez Gelato"). */
  providerDisplayName?: string
}

interface BadgeStyleSpec {
  variant: BadgeVariant
  dot: boolean
}

const STATUS_STYLE: Record<PodProductStatus, BadgeStyleSpec> = {
  draft: { variant: 'default', dot: true },
  uploading: { variant: 'info', dot: true },
  ready: { variant: 'success', dot: true },
  failed: { variant: 'danger', dot: false },
  archived: { variant: 'default', dot: false },
}

export default function PodStatusBadge({ status, providerDisplayName }: PodStatusBadgeProps) {
  const style = STATUS_STYLE[status]
  const label = POD_PRODUCT_STATUS_FR[status]
  const title = providerDisplayName ? `${label} chez ${providerDisplayName}` : label
  return (
    <Badge variant={style.variant} dot={style.dot} size="sm" title={title}>
      POD · {label}
    </Badge>
  )
}
